import { io, Socket } from 'socket.io-client';

import { ensureFreshAccessToken, isExpired } from '@shared/store/api/baseApi';
import { resolveSocketIoConnection } from '@shared/utils/serviceUrl';

// The gateways check handshake.auth.token on every message, so a rejected one means the handshake
// is stale: reconnect (the auth callback reads a fresh token) instead of dropping the socket.
const UNAUTHORIZED_MESSAGES = new Set(['Unauthorized', 'Missing token']);
const AUTH_RETRY_COOLDOWN_MS = 10_000;
// A server that keeps rejecting the handshake is not retried forever; the next acquire tries once more
const MAX_FAILED_HANDSHAKES = 5;
// Same for a token the gateways keep rejecting. A rejection after a quiet minute is a new token
// expiry, not the same failure repeating, so it starts a new count.
const MAX_AUTH_RETRIES = 5;
const AUTH_FAILURE_RESET_MS = 60_000;

const isRecoverableAuthException = (error: unknown): boolean => {
  const message = (error as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' && UNAUTHORIZED_MESSAGES.has(message);
};

interface Room {
  event: string;
  payload: Record<string, unknown>;
  count: number;
}

interface Connection {
  socket: Socket;
  refCount: number;
  // The server has no unsubscribe: a left room stays joined until the socket closes, so every
  // consumer filters its pushes by id (conversationId, campaignId, customer.campaignId)
  rooms: Map<string, Room>;
  handshakeToken: string | null;
  // Once connected, every later connect follows a gap (network, server, token renewal, auth retry)
  // in which pushes were lost
  hasConnected: boolean;
  missedPushesListeners: Set<() => void>;
  authRetryTimer: ReturnType<typeof setTimeout> | null;
  lastAuthRetryAt: number;
  failedHandshakes: number;
  failedAuthRetries: number;
  // Out of auth retries: the socket is closed and the rejection is reported to the hooks
  authRetriesExhausted: boolean;
}

export interface RealtimeSocketHandle {
  on: <T = unknown>(event: string, listener: (payload: T) => void) => () => void;
  // Called after a reconnect, once the rooms are joined again: pushes sent during the gap are not
  // replayed, so the data they keep fresh has to be reloaded. Invalidate tags rather than refetch:
  // RTK skips a refetch while a request is in flight, but holds an invalidation until it settles
  onMissedPushes: (listener: () => void) => () => void;
  isConnected: () => boolean;
  joinRoom: (event: string, payload: Record<string, unknown>) => () => void;
  release: () => void;
}

const connections = new Map<string, Connection>();

// Sorted entries, so { a, b } and { b, a } are the same room however the payload was built
const toRoomKey = (event: string, payload: Record<string, unknown>) =>
  `${event}:${JSON.stringify(Object.entries(payload).sort(([a], [b]) => a.localeCompare(b)))}`;

const emitRooms = ({ socket, rooms }: Connection) => {
  rooms.forEach(({ event, payload }) => socket.emit(event, payload));
};

const openSocket = (connection: Connection) => {
  void ensureFreshAccessToken().then((token) => {
    // No session (refresh failed and signed out): stay closed, the next acquire tries again
    if (!token || connection.refCount === 0 || connection.socket.active) return;
    connection.authRetriesExhausted = false;
    connection.socket.connect();
  });
};

const reconnect = (connection: Connection) => {
  connection.socket.disconnect();
  openSocket(connection);
};

const retryAuth = (connection: Connection) => {
  if (connection.authRetryTimer) return;

  const wait = Math.max(0, connection.lastAuthRetryAt + AUTH_RETRY_COOLDOWN_MS - Date.now());
  connection.authRetryTimer = setTimeout(() => {
    connection.authRetryTimer = null;
    if (connection.refCount === 0) return;

    connection.lastAuthRetryAt = Date.now();
    reconnect(connection);
  }, wait);
};

const createConnection = (url: string, path: string, namespace: string): Connection => {
  const socket = io(`${url}${namespace}`, {
    path: `${path}/socket.io`,
    transports: ['websocket'],
    autoConnect: false,
    // Runs on every handshake, including socket.io's own reconnects after a network drop
    auth: (cb) => {
      void ensureFreshAccessToken().then((token) => {
        connection.handshakeToken = token;
        cb({ token });
      });
    },
  });

  const connection: Connection = {
    socket,
    refCount: 0,
    rooms: new Map(),
    handshakeToken: null,
    hasConnected: false,
    missedPushesListeners: new Set(),
    authRetryTimer: null,
    lastAuthRetryAt: 0,
    failedHandshakes: 0,
    failedAuthRetries: 0,
    authRetriesExhausted: false,
  };

  socket.on('connect', () => {
    if (!connection.handshakeToken) {
      socket.disconnect();
      return;
    }

    connection.failedHandshakes = 0;
    emitRooms(connection);

    const missedPushes = connection.hasConnected;
    connection.hasConnected = true;
    if (missedPushes) connection.missedPushesListeners.forEach((listener) => listener());
  });
  socket.on('exception', (error: unknown) => {
    // Every room re-joined with a rejected token answers with its own exception: count one retry
    if (!isRecoverableAuthException(error) || connection.authRetryTimer) return;
    if (connection.authRetriesExhausted) return;

    if (Date.now() - connection.lastAuthRetryAt > AUTH_FAILURE_RESET_MS) {
      connection.failedAuthRetries = 0;
    }
    connection.failedAuthRetries += 1;
    if (connection.failedAuthRetries > MAX_AUTH_RETRIES) {
      // Realtime is gone until the next acquire: hooks see the disconnect and get this exception
      connection.authRetriesExhausted = true;
      socket.disconnect();
      return;
    }
    retryAuth(connection);
  });
  // A transport error is retried by socket.io itself; a rejected handshake leaves the socket inactive
  socket.on('connect_error', () => {
    if (socket.active) return;

    connection.failedHandshakes += 1;
    if (connection.failedHandshakes > MAX_FAILED_HANDSHAKES) return;
    retryAuth(connection);
  });

  return connection;
};

/**
 * One Socket.IO connection per server + namespace, shared by every hook that acquires it.
 * Rooms are re-joined on every (re)connect, so hooks join through the handle instead of emitting.
 */
export const acquireRealtimeSocket = (apiUrl: string, namespace = ''): RealtimeSocketHandle => {
  const { url, path } = resolveSocketIoConnection(apiUrl);
  const key = `${url}|${path}|${namespace}`;
  const connection = connections.get(key) ?? createConnection(url, path, namespace);
  connections.set(key, connection);
  connection.refCount += 1;

  if (!connection.socket.active) openSocket(connection);

  let released = false;

  const on: RealtimeSocketHandle['on'] = (event, listener) => {
    const wrapped = (payload: Parameters<typeof listener>[0]) => {
      // Handled here: the hooks would only report an error that is already being fixed
      if (
        event === 'exception' &&
        isRecoverableAuthException(payload) &&
        !connection.authRetriesExhausted
      ) {
        return;
      }
      listener(payload);
    };

    connection.socket.on(event, wrapped);
    return () => {
      connection.socket.off(event, wrapped);
    };
  };

  const onMissedPushes: RealtimeSocketHandle['onMissedPushes'] = (listener) => {
    connection.missedPushesListeners.add(listener);
    return () => {
      connection.missedPushesListeners.delete(listener);
    };
  };

  const joinRoom: RealtimeSocketHandle['joinRoom'] = (event, payload) => {
    const roomKey = toRoomKey(event, payload);
    const room = connection.rooms.get(roomKey);

    if (room) {
      room.count += 1;
    } else {
      connection.rooms.set(roomKey, { event, payload, count: 1 });

      // A disconnected socket would buffer the emit; 'connect' joins all rooms anyway
      if (connection.socket.connected) {
        if (connection.handshakeToken && isExpired(connection.handshakeToken)) {
          // The join would be rejected; a fresh handshake re-joins every room
          reconnect(connection);
        } else {
          connection.socket.emit(event, payload);
        }
      }
    }

    let left = false;

    return () => {
      if (left) return;
      left = true;

      const current = connection.rooms.get(roomKey);
      if (!current) return;

      current.count -= 1;
      if (current.count > 0) return;

      // Still joined on the server until the socket closes; the consumers filter those pushes out
      connection.rooms.delete(roomKey);
    };
  };

  const release = () => {
    if (released) return;
    released = true;

    connection.refCount -= 1;
    if (connection.refCount > 0) return;

    if (connection.authRetryTimer) clearTimeout(connection.authRetryTimer);
    connection.socket.disconnect();
    connections.delete(key);
  };

  return {
    on,
    onMissedPushes,
    isConnected: () => connection.socket.connected,
    joinRoom,
    release,
  };
};
